// Голосовой оркестратор: одно состояние на приложение.
//
// Стор живёт на уровне модуля, а не компонента: разговор не рвётся, когда
// десктоп прячет раздел (keepAlive) или телефон уходит с экрана оркестратора
// назад по стеку. Десктопный раздел и мобильный экран читают один и тот же стор.
//
// Протокол — docs/orchestrator-spec.md:
//   WS /api/orchestrator/ws?token=… — бинарные кадры PCM16LE 24 кГц в обе стороны;
//   сервер → { type: state | transcript | tool | interrupt | error | brain };
//   клиент → { type: stop | text | played }.
//   REST: GET status, PUT/DELETE key, GET history, GET history/{id}.

import { create } from 'zustand'
import { getToken } from '../../api'
import { isMicBusy, subscribeMicBusy } from '../../lib/micBusy'
import { describe, http } from '../custom/http'
import { AudioEngine, createContext } from './audio'
import { dropsAudio, manualToggle, recordingStep } from './autoMute'

export type OrchStatus = 'off' | 'connecting' | 'listening' | 'thinking' | 'speaking' | 'error'

export interface OrchItem {
  id: number
  kind: 'user' | 'assistant' | 'tool'
  text: string
  /** реплика дописана (для user/assistant) */
  final: boolean
  /** id элемента разговора OpenAI — по нему сливаются кадры одной реплики */
  key?: string
  /** для строк инструментов */
  tool?: { name: string; status: 'start' | 'done' | 'error'; sessionId?: string }
}

export interface OrchBrainModel { id: string; label: string }

/** Проект оркестратора (v5): отдельная беседа «Проект: …» на большую задачу. */
export interface OrchProject {
  id: string
  sessionId: string | null
  title: string
  /** planning | awaiting_approval | running | done | paused */
  status: string
  statusRu: string
  /** состояние беседы проекта (idle | running | waiting_permission | exited) */
  state?: string | null
  updatedAt?: number | null
  planPath?: string | null
  legacy?: boolean
}

export interface OrchKeyStatus {
  hasKey: boolean
  model?: string
  /** голос активного движка: применяется со следующего «Запустить» */
  voice?: string
  /** голоса активного движка */
  voices?: string[]
  personaPath?: string
  /** движок голоса: live (GPT-Live + мозг Claude) | realtime (v2) */
  engine?: string
  engines?: string[]
  /** модель первой линии — быстрого мозга (алиас claude: opus | sonnet | haiku) */
  frontModel?: string
  /** модель проектов (новые беседы «Проект: …») */
  plannerModel?: string
  brainModels?: OrchBrainModel[]
  /** сессии первой линии и планировщика в «Беседах» */
  frontSessionId?: string | null
  plannerSessionId?: string | null
  /** проекты из реестра, свежие сверху */
  projects?: OrchProject[]
  /** непрочитанные обновления рабочих бесед (пока разговор не был открыт) */
  inbox?: number
}

export interface OrchHistoryEntry { id: string; label: string; when: string }

/** Путь заметки персоны в Базе (создаёт сервер при первом запуске). */
export const PERSONA_NOTE = 'личное/Оркестратор/Ассистент.md'

/** Как часто отправлять «played», пока звучит ответ. */
const PLAYED_EVERY_MS = 250

interface OrchState {
  status: OrchStatus
  /** пояснение к состоянию («подключаюсь к OpenAI…») */
  detail: string
  error: string | null
  muted: boolean
  /** микрофон выключили мы, потому что пишется голосовое (lib/micBusy);
   *  кончится запись — включим обратно. Ручной мут сюда не попадает. */
  autoMuted: boolean
  /** где-то в панели (или в другой вкладке) идёт запись голоса */
  recording: boolean
  items: OrchItem[]
  key: OrchKeyStatus | null
  keyError: string | null
  history: OrchHistoryEntry[]
  historyError: string | null
  /** открытый прошлый разговор (вместо живой ленты) */
  viewing: string | null
  viewItems: OrchItem[]
  viewLoading: boolean

  loadStatus: () => Promise<void>
  saveKey: (key: string) => Promise<boolean>
  /** Голос для следующего разговора (посреди разговора OpenAI голос не меняет). */
  setVoice: (voice: string) => Promise<void>
  /** Движок голоса — со следующего «Запустить». */
  setEngine: (engine: string) => Promise<void>
  /** Модель первой линии или планировщика — сервер меняет её сразу, если сессия поднята. */
  setBrainModel: (which: 'front' | 'planner', model: string) => Promise<void>
  /** голос выбран, пока шёл разговор — подействует со следующего запуска */
  voicePending: boolean
  deleteKey: () => Promise<void>
  loadHistory: () => Promise<void>
  openHistory: (id: string) => Promise<void>
  closeHistory: () => void
  /** Звать синхронно из обработчика клика (iOS). */
  start: () => void
  stop: () => void
  toggleMute: () => void
  /** Запись голоса вне оркестратора началась/кончилась — зовёт подписка на micBusy. */
  syncRecording: (recording: boolean) => void
  sendText: (text: string) => boolean
  /** Завершить текущий, очистить ленту и сразу начать новый (из клика). */
  newConversation: () => void
}

// ---------------- живое соединение (вне React) ----------------

let ws: WebSocket | null = null
let engine: AudioEngine | null = null
let playedTimer = 0
let lastPlayed = -1
let nextId = 1
/** stop() вызван нами — закрытие сокета не ошибка */
let stopping = false
/** ответ прерван записью голосового: его хвост, который сервер ещё досылает,
 *  не играем до смены состояния (listening/thinking) */
let dropTail = false
/** v7.4: кадр interrupt за эту запись голосового уже ушёл — живой хвост ответа
 *  (Live шлёт звук непрерывно) не должен слать его снова после каждого listening */
let interruptSent = false

/** Уровни для анимации круга: читаются в requestAnimationFrame, не через стор. */
export function levels(): { mic: number; out: number } {
  if (!engine) return { mic: 0, out: 0 }
  return { mic: engine.micLevel, out: engine.outLevel() }
}

function wsUrl(): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
  const token = encodeURIComponent(getToken() ?? '')
  return `${proto}//${location.host}/api/orchestrator/ws?token=${token}`
}

function sendJson(data: unknown): void {
  if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(data))
}

function reportPlayed(force = false): void {
  if (!engine) return
  const ms = engine.playedMs()
  if (!force && ms === lastPlayed) return
  lastPlayed = ms
  sendJson({ type: 'played', ms })
}

/** Коды закрытия сервера, которые стоит назвать словами. */
const CLOSE_TEXT: Record<number, string> = {
  4401: 'сессия оператора истекла — войдите в панель заново',
  4403: 'нет доступа к оркестратору',
  4409: 'не задан ключ OpenAI API',
}

const STATES: OrchStatus[] = ['connecting', 'listening', 'thinking', 'speaking', 'error']

/** Строка инструмента «⎿ …»: id беседы, если сервер его прислал. */
function toolSessionId(frame: Record<string, unknown>): string | undefined {
  for (const k of ['sessionId', 'session_id', 'agentId']) {
    const v = frame[k]
    if (typeof v === 'string' && v) return v
  }
  return undefined
}

// ---------------- разбор журнала прошлых разговоров ----------------

function asItems(raw: unknown): OrchItem[] {
  let list: unknown = raw
  if (list && !Array.isArray(list) && typeof list === 'object') {
    const obj = list as Record<string, unknown>
    list = obj.items ?? obj.entries ?? obj.events ?? obj.lines ?? obj.messages ?? []
  }
  if (!Array.isArray(list)) return []
  const out: OrchItem[] = []
  for (const entry of list) {
    if (!entry || typeof entry !== 'object') continue
    const e = entry as Record<string, unknown>
    const type = typeof e.type === 'string' ? e.type : typeof e.kind === 'string' ? e.kind : ''
    if (type === 'event' && e.name === 'project') {
      const ps = String(e.projectStatus ?? '')
      out.push({
        id: nextId++, kind: 'tool', final: true,
        text: `проект «${String(e.title ?? '')}»: ${ps === 'awaiting_approval' ? 'план готов, ждёт «да»' : String(e.status ?? 'обновление')}`,
        tool: { name: 'project', status: e.status === 'failed' ? 'error' : 'done', sessionId: toolSessionId(e) },
      })
      continue
    }
    if (type === 'event' && e.name === 'planner') {
      const st = String(e.status ?? 'done')
      out.push({
        id: nextId++, kind: 'tool', final: true,
        text: st === 'done' ? 'планировщик: план готов' : st === 'timeout' ? 'планировщик: работает дольше обычного' : `планировщик: ${st}`,
        tool: { name: 'planner', status: st === 'error' ? 'error' : 'done', sessionId: toolSessionId(e) },
      })
      continue
    }
    if (type === 'event' && e.name === 'promise') {
      // v6: проверка обещаний первой линии («я обещал …»)
      const failed = e.status === 'failed'
      out.push({
        id: nextId++, kind: 'tool', final: true,
        text: failed ? 'проверка обещаний: не выполнено' : 'проверка обещаний: напоминание',
        tool: { name: 'promise', status: failed ? 'error' : 'done' },
      })
      continue
    }
    if (type === 'guard') {
      // v6: голос сказал «сделал» до ответа мозга — сервер велел поправиться
      out.push({
        id: nextId++, kind: 'tool', final: true,
        text: `голос поторопился: «${String(e.claim ?? '')}» — поправлен`,
        tool: { name: 'guard', status: 'error' },
      })
      continue
    }
    if (type === 'verify') {
      // v6: ответ мозга сверен с вызовами инструментов
      const why = e.why === 'silent' ? 'мозг промолчал на просьбу — переспросил' : 'мозг написал «сделано» без вызова — переспросил'
      const promised = Array.isArray(e.promises) && e.promises.length ? 'обещание записано в реестр' : ''
      const text = e.reasked ? why : promised
      if (text) out.push({ id: nextId++, kind: 'tool', final: true, text, tool: { name: 'verify', status: e.status === 'unbacked' ? 'error' : 'done' } })
      continue
    }
    if (type === 'event') {
      if (e.name !== 'agent_done' && e.name !== 'agent_update') continue
      const title = typeof e.title === 'string' ? e.title : 'агент'
      const failed = e.status === 'failed' || e.failed === true
      out.push({
        id: nextId++, kind: 'tool',
        text: e.name === 'agent_update' ? `«${title}»: обновление` : `агент «${title}» закончил`, final: true,
        tool: { name: String(e.name), status: failed ? 'error' : 'done', sessionId: toolSessionId(e) },
      })
      continue
    }
    if (type === 'delegation') {
      // запрос голоса к мозгу: сколько думал и чем кончилось
      const secs = typeof e.seconds === 'number' ? ` за ${Math.round(e.seconds)} с` : ''
      const st = String(e.status ?? 'done')
      if (e.event === true) {
        // ход первой линии по событию (обновление беседы, итог планировщика)
        if (st === 'superseded' || st === 'cancelled') continue
        const text = st === 'done' ? `первая линия разобрала событие${secs}` : st === 'silent' ? 'событие без озвучки' : `событие: ${st}`
        out.push({ id: nextId++, kind: 'tool', text, final: true, tool: { name: 'brain', status: st === 'error' ? 'error' : 'done' } })
        continue
      }
      const text = st === 'done' ? `мозг ответил${secs}` : st === 'superseded' ? 'уточнение — прошлый запрос прерван'
        : st === 'timeout' ? 'мозг не уложился — ответ позже' : st === 'late' ? 'поздний ответ мозга'
        : st === 'silent' ? 'мозг решил промолчать' : st === 'unbacked' ? 'мозг: «сделано» без действия — голосу «не сделано»'
        : `мозг: ${st}`
      out.push({ id: nextId++, kind: 'tool', text, final: true, tool: { name: 'brain', status: st === 'error' ? 'error' : 'done' } })
      continue
    }
    if (type === 'usage') continue
    if (type === 'tool' || (typeof e.name === 'string' && !e.role)) {
      const status = e.status === 'done' || e.status === 'error' ? e.status : 'start'
      const name = String(e.name ?? 'инструмент')
      const text = String(e.summary ?? e.text ?? name)
      // в журнале обычно есть и start, и done — оставляем итоговую строку
      const prev = out[out.length - 1]
      if (prev?.kind === 'tool' && prev.tool?.name === name && prev.tool.status === 'start') out.pop()
      out.push({ id: nextId++, kind: 'tool', text, final: true, tool: { name, status, sessionId: toolSessionId(e) } })
      continue
    }
    const role = e.role === 'user' || e.role === 'assistant' ? e.role : null
    const text = typeof e.text === 'string' ? e.text : ''
    if (!role || !text.trim() || e.final === false) continue
    // шум из старых журналов («어»): без кириллицы и латиницы — не реплика
    if (role === 'user' && !/[0-9a-zа-яё]/i.test(text)) continue
    out.push({ id: nextId++, kind: role, text, final: true })
  }
  return out
}

function pick(obj: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = obj[k]
    if (typeof v === 'string' && v.trim()) return v
    if (typeof v === 'number') return String(Math.round(v))
  }
  return ''
}

/** «20251020-081500» или ISO → «20 окт, 08:15». */
function whenText(raw: string, id: string): string {
  if (/^\d{9,}$/.test(raw)) {
    const n = Number(raw)
    raw = new Date(n < 1e12 ? n * 1000 : n).toISOString()
  }
  const m = /^(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})/.exec(raw || id)
  const d = m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5])) : new Date(raw)
  if (Number.isNaN(d.getTime())) return raw
  // id журнала уже в московском времени (сервер так называет файлы) — сдвиг не нужен
  const tz = m && !raw.includes('T') ? 'UTC' : 'Europe/Moscow'
  return d.toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: tz })
}

function asHistory(raw: unknown): OrchHistoryEntry[] {
  let list: unknown = raw
  if (list && !Array.isArray(list) && typeof list === 'object') {
    const obj = list as Record<string, unknown>
    list = obj.items ?? obj.conversations ?? obj.history ?? []
  }
  if (!Array.isArray(list)) return []
  return list.flatMap((x) => {
    if (!x || typeof x !== 'object') return []
    const e = x as Record<string, unknown>
    const id = pick(e, ['id', 'name'])
    if (!id) return []
    let label = pick(e, ['title', 'preview', 'first', 'summary', 'text'])
    // сервер отдаёт preview списком первых реплик { role, text }: берём первую реплику Степана
    const lines = Array.isArray(e.preview) ? e.preview : Array.isArray(e.lines) ? e.lines : null
    if (!label && lines) {
      const texts = lines.map((v) => (typeof v === 'string' ? { role: '', text: v }
        : v && typeof v === 'object' ? { role: String((v as Record<string, unknown>).role ?? ''), text: String((v as Record<string, unknown>).text ?? '') } : null))
        .filter((v): v is { role: string; text: string } => !!v && !!v.text.trim())
      label = (texts.find((v) => v.role === 'user') ?? texts[0])?.text ?? ''
    }
    label = label.replace(/\s+/g, ' ').trim()
    const when = whenText(pick(e, ['startedAt', 'started', 'time', 'ts', 'created']), id)
    return [{ id, label: label || 'без реплик', when }]
  })
}

// ---------------- стор ----------------

export const useOrchestrator = create<OrchState>((set, get) => {
  /** Слить кадр транскрипта в ленту. Не-финальные кадры — дельты или
   *  накопленный текст: накопленный узнаём по совпадению начала. */
  const onTranscript = (role: 'user' | 'assistant', text: string, final: boolean, key?: string) => {
    set((s) => {
      const items = [...s.items]
      let index = -1
      if (key) {
        // реплику с известным id ищем по нему — даже закрытую перебиванием
        for (let i = items.length - 1; i >= 0 && i >= items.length - 40; i--) {
          if (items[i].kind === role && items[i].key === key) { index = i; break }
        }
      }
      if (index < 0) {
        for (let i = items.length - 1; i >= 0 && i >= items.length - 4; i--) {
          if (items[i].kind === role && !items[i].final && (!key || !items[i].key)) { index = i; break }
        }
      }
      if (index >= 0) {
        const cur = items[index]
        const merged = final ? (text || cur.text) : text.startsWith(cur.text) ? text : cur.text + text
        // перебитую реплику («…») поздний итог сервера не продлевает обратно
        if (cur.final && !final) return {}
        items[index] = { ...cur, text: cur.final && cur.text.endsWith('…') ? cur.text : merged, final: final || cur.final, key: key ?? cur.key }
        return { items }
      }
      if (!text.trim()) return {}
      // Свой текст из поля ввода уже стоит в ленте — эхо сервера не дублируем.
      if (role === 'user') {
        const last = [...items].reverse().find((x) => x.kind === 'user')
        if (last && last.final && last.text.trim() === text.trim()) return {}
      }
      const item: OrchItem = { id: nextId++, kind: role, text, final, key }
      // Расшифровка речи Степана приходит, когда ассистент уже начал отвечать:
      // ставим её перед открытым ответом, чтобы порядок был естественным.
      const tail = items[items.length - 1]
      if (role === 'user' && tail && tail.kind === 'assistant' && !tail.final) items.splice(items.length - 1, 0, item)
      else items.push(item)
      return { items }
    })
  }

  const onTool = (frame: Record<string, unknown>) => {
    const name = String(frame.name ?? 'инструмент')
    const status = frame.status === 'done' || frame.status === 'error' ? frame.status : 'start'
    const summary = String(frame.summary ?? name)
    const sessionId = toolSessionId(frame)
    set((s) => {
      const items = [...s.items]
      if (status !== 'start') {
        for (let i = items.length - 1; i >= 0; i--) {
          const it = items[i]
          if (it.kind === 'tool' && it.tool?.name === name && it.tool.status === 'start') {
            items[i] = { ...it, text: summary || it.text, tool: { name, status, sessionId: sessionId ?? it.tool.sessionId } }
            return { items }
          }
        }
      }
      items.push({ id: nextId++, kind: 'tool', text: summary, final: true, tool: { name, status, sessionId } })
      return { items }
    })
  }

  const onInterrupt = () => {
    reportPlayed(true)
    engine?.flush()
    set((s) => ({
      items: s.items.map((it) => (it.kind === 'assistant' && !it.final ? { ...it, final: true, text: `${it.text}…` } : it)),
    }))
  }

  /** Запись голосового: заглушить звучащий ответ, как при перебивании.
   *  Сервер узнаёт, сколько прозвучало (played), его хвост мы не играем. */
  const silenceForRecording = () => {
    if (!engine) return
    const playing = engine.isPlaying() || get().status === 'speaking'
    if (!playing) return
    cutForRecording()
  }

  /** Оборвать ответ ради записи: сказать серверу, сколько прозвучало, хвост не играть.
   *  v7.4: и кадр interrupt — сервер отменяет ответ (realtime) / велит голосу замолчать (live). */
  const cutForRecording = () => {
    reportPlayed(true)
    if (!interruptSent) {
      interruptSent = true
      sendJson({ type: 'interrupt' })
    }
    engine?.flush()
    dropTail = true
    set((s) => ({
      items: s.items.map((it) => (it.kind === 'assistant' && !it.final ? { ...it, final: true, text: `${it.text}…` } : it)),
    }))
  }

  const onMessage = (event: MessageEvent) => {
    if (event.data instanceof ArrayBuffer) {
      const s = get()
      // ответ пошёл, когда запись уже шла (был thinking или ответ начался сам):
      // он тоже оборван — его остаток после записи не играем
      if (s.recording && s.muted && !dropTail) cutForRecording()
      if (dropsAudio(s.recording, s.muted, dropTail)) return
      engine?.play(event.data)
      return
    }
    let frame: Record<string, unknown>
    try {
      frame = JSON.parse(String(event.data)) as Record<string, unknown>
    } catch {
      return
    }
    switch (frame.type) {
      case 'state': {
        const state = STATES.includes(frame.state as OrchStatus) ? (frame.state as OrchStatus) : null
        if (!state) return
        if (state === 'thinking') engine?.markNewResponse()
        // прерванный записью ответ кончился — следующий снова слышно
        if (state === 'thinking' || state === 'listening') dropTail = false
        // сервер не нашёл ключ (удалили, пока разговор открывался) — показать карточку ключа
        if (state === 'error' && frame.detail === 'no_key') void get().loadStatus()
        // detail сервера — машинный код (no_key, upstream); показываем только человеческий текст
        const detail = typeof frame.detail === 'string' && /[а-яё]/i.test(frame.detail) ? frame.detail : ''
        set({ status: state, detail })
        return
      }
      case 'transcript': {
        const role = frame.role === 'user' ? 'user' : frame.role === 'assistant' ? 'assistant' : null
        const key = typeof frame.id === 'string' && frame.id ? frame.id : undefined
        if (!role) return
        // сервер признал реплику шумом — убрать её черновик из ленты
        if (frame.noise === true) {
          if (key) set((s) => ({ items: s.items.filter((it) => !(it.kind === role && it.key === key)) }))
          return
        }
        onTranscript(role, String(frame.text ?? ''), frame.final === true, key)
        return
      }
      case 'tool':
        onTool(frame)
        return
      case 'interrupt':
        onInterrupt()
        return
      case 'brain': {
        // мозг поднят — ссылка «открыть … в Беседах» (role: front | planner)
        const sid = typeof frame.sessionId === 'string' ? frame.sessionId : null
        const key = get().key
        if (sid && key) {
          set({ key: frame.role === 'planner' ? { ...key, plannerSessionId: sid } : { ...key, frontSessionId: sid, inbox: 0 } })
        }
        return
      }
      case 'project':
        // проект заведён или обновился — список проектов перечитываем
        void get().loadStatus()
        return
      case 'error':
        set({ error: String(frame.message ?? 'ошибка оркестратора') })
        return
      default:
    }
  }

  /** Погасить всё: сокет, микрофон, контекст. Состояние — off или error. */
  const teardown = (error: string | null) => {
    window.clearInterval(playedTimer)
    playedTimer = 0
    const sock = ws
    ws = null
    if (sock) {
      sock.onopen = null
      sock.onmessage = null
      sock.onclose = null
      sock.onerror = null
      if (sock.readyState === WebSocket.OPEN) {
        try { sock.send(JSON.stringify({ type: 'stop' })) } catch { /* уже закрыт */ }
      }
      if (sock.readyState === WebSocket.OPEN || sock.readyState === WebSocket.CONNECTING) sock.close(1000)
    }
    engine?.close()
    engine = null
    lastPlayed = -1
    dropTail = false
    interruptSent = false
    set((s) => ({
      voicePending: false,
      status: error ? 'error' : 'off',
      detail: '',
      error: error ?? s.error,
      muted: false,
      autoMuted: false,
      items: s.items.map((it) => (it.final ? it : { ...it, final: true })),
    }))
    // в журнале появился (или дописался) разговор
    void get().loadHistory()
  }

  return {
    status: 'off',
    detail: '',
    error: null,
    muted: false,
    autoMuted: false,
    recording: isMicBusy(),
    items: [],
    key: null,
    keyError: null,
    voicePending: false,
    history: [],
    historyError: null,
    viewing: null,
    viewItems: [],
    viewLoading: false,

    loadStatus: async () => {
      try {
        const key = await http<OrchKeyStatus>('/orchestrator/status')
        set({
          key: {
            hasKey: !!key.hasKey,
            model: key.model,
            voice: key.voice,
            voices: Array.isArray(key.voices) ? key.voices.filter((v): v is string => typeof v === 'string') : undefined,
            personaPath: key.personaPath,
            engine: key.engine,
            engines: Array.isArray(key.engines) ? key.engines : undefined,
            frontModel: key.frontModel,
            plannerModel: key.plannerModel,
            brainModels: Array.isArray(key.brainModels) ? key.brainModels : undefined,
            frontSessionId: key.frontSessionId ?? null,
            plannerSessionId: key.plannerSessionId ?? null,
            projects: Array.isArray(key.projects) ? key.projects : [],
            inbox: typeof key.inbox === 'number' ? key.inbox : 0,
          },
          keyError: null,
        })
      } catch (error) {
        set({ keyError: describe(error) })
      }
    },

    saveKey: async (key) => {
      const value = key.trim()
      if (!value.startsWith('sk-')) {
        set({ keyError: 'ключ OpenAI начинается с «sk-»' })
        return false
      }
      try {
        await http('/orchestrator/key', { method: 'PUT', body: JSON.stringify({ key: value }) })
        set({ keyError: null })
        await get().loadStatus()
        return true
      } catch (error) {
        set({ keyError: describe(error) })
        return false
      }
    },

    setVoice: async (voice) => {
      const prev = get().key
      if (!prev || prev.voice === voice) return
      set({ key: { ...prev, voice }, voicePending: isLive(get().status) })
      try {
        await http('/orchestrator/settings', { method: 'PUT', body: JSON.stringify({ voice }) })
        set({ keyError: null })
      } catch (error) {
        set({ key: prev, voicePending: false, error: `голос не сохранился: ${describe(error)}` })
      }
    },

    setEngine: async (engine) => {
      const prev = get().key
      if (!prev || prev.engine === engine) return
      set({ key: { ...prev, engine }, voicePending: isLive(get().status) })
      try {
        await http('/orchestrator/settings', { method: 'PUT', body: JSON.stringify({ engine }) })
        // у движков разные списки голосов — перечитать
        await get().loadStatus()
      } catch (error) {
        set({ key: prev, voicePending: false, error: `движок не сохранился: ${describe(error)}` })
      }
    },

    setBrainModel: async (which, model) => {
      const prev = get().key
      const field = which === 'planner' ? 'plannerModel' : 'frontModel'
      if (!prev || prev[field] === model) return
      set({ key: { ...prev, [field]: model } })
      try {
        await http('/orchestrator/settings', { method: 'PUT', body: JSON.stringify({ [field]: model }) })
      } catch (error) {
        set({ key: prev, error: `модель не сохранилась: ${describe(error)}` })
      }
    },

    deleteKey: async () => {
      if (get().status !== 'off' && get().status !== 'error') get().stop()
      try {
        await http('/orchestrator/key', { method: 'DELETE' })
      } catch (error) {
        set({ keyError: describe(error) })
      }
      await get().loadStatus()
    },

    loadHistory: async () => {
      try {
        const raw = await http<unknown>('/orchestrator/history?limit=20')
        set({ history: asHistory(raw), historyError: null })
      } catch (error) {
        set({ historyError: describe(error) })
      }
    },

    openHistory: async (id) => {
      set({ viewing: id, viewItems: [], viewLoading: true })
      try {
        const raw = await http<unknown>(`/orchestrator/history/${encodeURIComponent(id)}`)
        if (get().viewing === id) set({ viewItems: asItems(raw), viewLoading: false })
      } catch (error) {
        if (get().viewing === id) {
          set({ viewItems: [{ id: nextId++, kind: 'tool', text: `не открылся: ${describe(error)}`, final: true }], viewLoading: false })
        }
      }
    },

    closeHistory: () => set({ viewing: null, viewItems: [], viewLoading: false }),

    start: () => {
      if (ws || engine) {
        if (isLive(get().status)) return
        // сервер сообщил об ошибке, но сокет ещё не закрыл — гасим и начинаем заново
        stopping = true
        teardown(null)
      }
      stopping = false
      let ctx: AudioContext
      try {
        ctx = createContext()
      } catch (error) {
        set({ status: 'error', error: describe(error) })
        return
      }
      const sock = new WebSocket(wsUrl())
      sock.binaryType = 'arraybuffer'
      ws = sock
      const eng = new AudioEngine(ctx, {
        onFrame: (pcm) => {
          if (ws === sock && sock.readyState === WebSocket.OPEN) sock.send(pcm)
        },
      })
      engine = eng
      // запускают, пока пишется голосовое — сразу с выключенным микрофоном
      const recording = isMicBusy()
      if (recording) eng.setMuted(true)
      set({
        status: 'connecting', detail: 'включаю микрофон…', error: null,
        muted: recording, autoMuted: recording, recording, viewing: null, viewItems: [],
      })

      sock.onopen = () => {
        if (get().status === 'connecting') set({ detail: 'подключаюсь к ассистенту…' })
      }
      sock.onmessage = onMessage
      sock.onerror = () => { /* причину скажет onclose */ }
      sock.onclose = (event) => {
        if (ws !== sock) return
        if (stopping) { teardown(null); return }
        const known = CLOSE_TEXT[event.code]
        const reason = event.reason ? `: ${event.reason}` : ''
        teardown(known ?? (event.code === 1000 ? null : `связь с сервером оборвалась${reason} — нажмите «Запустить» ещё раз`))
      }

      eng.startCapture().catch((error) => {
        if (engine !== eng) return
        teardown(describe(error))
      })

      // «played» — периодически, пока звучит ответ; сервер берёт последний для truncate
      playedTimer = window.setInterval(() => {
        if (engine?.isPlaying()) reportPlayed()
      }, PLAYED_EVERY_MS)
    },

    stop: () => {
      if (!ws && !engine) {
        set({ status: 'off', detail: '' })
        return
      }
      stopping = true
      teardown(null)
    },

    toggleMute: () => {
      // нажали руками во время записи — решение Степана, авто-мут снят
      const next = manualToggle(get())
      engine?.setMuted(next.muted)
      set(next)
    },

    syncRecording: (recording) => {
      const s = get()
      if (s.recording === recording) return
      if (!recording) interruptSent = false
      const step = recordingStep(s, recording, !!engine && isLive(s.status))
      if (step.silence) silenceForRecording()
      if (step.muted !== s.muted) engine?.setMuted(step.muted)
      set({ recording, muted: step.muted, autoMuted: step.autoMuted })
    },

    sendText: (text) => {
      const value = text.trim()
      if (!value || !ws || ws.readyState !== WebSocket.OPEN) return false
      // Набранный вопрос перебивает звучащий ответ, как и голос.
      if (engine?.isPlaying()) {
        reportPlayed(true)
        engine.flush()
      }
      sendJson({ type: 'text', text: value })
      set((s) => ({ items: [...s.items, { id: nextId++, kind: 'user', text: value, final: true }] }))
      return true
    },

    newConversation: () => {
      get().stop()
      set({ items: [], error: null, viewing: null, viewItems: [] })
      if (get().key?.hasKey !== false) get().start()
    },
  }
})

/** Человеческая подпись состояния под кругом. */
export function statusText(status: OrchStatus, detail: string, muted: boolean, autoMuted = false): string {
  const recording = 'микрофон выключен — идёт запись голосового'
  switch (status) {
    case 'off': return 'выключен'
    case 'connecting': return detail || 'подключаюсь…'
    case 'listening': return autoMuted ? recording : muted ? 'микрофон выключен' : 'слушаю'
    case 'thinking': return detail || 'думаю…'
    case 'speaking': return autoMuted ? recording : 'говорит'
    case 'error': return 'ошибка'
  }
}

/** Разговор идёт (сокет открыт или открывается). */
export function isLive(status: OrchStatus): boolean {
  return status !== 'off' && status !== 'error'
}

// Запись голосового где угодно в панели (или в соседней вкладке) глушит
// микрофон ассистента, чтобы он не отвечал на то, что диктуют.
subscribeMicBusy((busy) => useOrchestrator.getState().syncRecording(busy))
